import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useAppDispatch } from "../../../../store";
import { AlertPopup } from "../../components/alert-popup/alert-popup";
import { AlertWrapper } from "../../components/alert-wrapper/alert-wrapper";
import { ConfirmDialog } from "../../components/confirm-dialog/confirm-dialog";
import { SnackbarStack } from "../../components/snackbar-stack/snackbar-stack";
import { DEFAULT_NOTIFICATION_TIMEOUT } from "../../shared/constants/notification-timeout";
import { INotification } from "../../shared/interfaces/notification";
import {
  getAlertNotifications,
  getConfirmNotification,
  getSnackNotifications,
} from "../../store/notifications.selector";
import { removeNotificationAction } from "../../store/notifications.slice";

const MAX_SNACK = 3;

export const NotificationsArea = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const confirm = useSelector(getConfirmNotification);
  const alerts = useSelector(getAlertNotifications);
  const snacks = useSelector(getSnackNotifications);
  const alertTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const [shownSnacks, setShownSnacks] = useState<INotification[]>([]);

  /**
   * Снеки живут в локальном состоянии: из стора забираем сразу, а срок показа
   * и стопку из последних MAX_SNACK ведёт уже SnackbarStack.
   */
  useEffect(() => {
    if (snacks.length === 0) {
      return;
    }

    setShownSnacks((shown) => {
      const shownIds = new Set(shown.map((snack) => snack.id));
      const added = snacks.filter((snack) => !shownIds.has(snack.id));
      return added.length > 0 ? [...shown, ...added].slice(-MAX_SNACK) : shown;
    });

    snacks.forEach((snack) => dispatch(removeNotificationAction(snack)));
  }, [snacks, dispatch]);

  const hideSnack = useCallback((snack: INotification) => {
    setShownSnacks((shown) => shown.filter((item) => item.id !== snack.id));
  }, []);

  /**
   * Таймер заводится один раз на уведомление и живёт по его id: соседнее
   * уведомление меняет список, а срок показа уже висящих не сдвигает.
   */
  useEffect(() => {
    const timers = alertTimers.current;

    for (const alert of alerts) {
      if (timers.has(alert.id)) {
        continue;
      }

      timers.set(
        alert.id,
        setTimeout(
          () => dispatch(removeNotificationAction(alert)),
          alert.timeout ?? DEFAULT_NOTIFICATION_TIMEOUT
        )
      );
    }

    const shownIds = new Set(alerts.map((alert) => alert.id));
    for (const [alertId, timer] of timers) {
      if (!shownIds.has(alertId)) {
        clearTimeout(timer);
        timers.delete(alertId);
      }
    }
  }, [alerts, dispatch]);

  useEffect(() => {
    const timers = alertTimers.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <>
      <SnackbarStack snacks={shownSnacks} onClose={hideSnack} />

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          onSuccess={confirm.cb}
          severity={confirm.severity}
          onClose={() => dispatch(removeNotificationAction(confirm))}
          confirmOnly={confirm.confirmOnly}
        />
      )}

      <AlertWrapper>
        {alerts.map((item, index) => (
          <AlertPopup
            key={`alert-popup-${index}`}
            title={item.title}
            message={item.message}
            severity={item.severity}
            onClose={() => dispatch(removeNotificationAction(item))}
          />
        ))}
      </AlertWrapper>
    </>
  );
};
